/**
 * Insights hierarchy views — two independent lenses over the same people scope.
 *
 * API values (stable): supply | reporting_line
 * User-facing labels below — keep jargon out of product copy.
 *
 * Keep in sync with teamform-api src/insights/lib/insightsHierarchyView.ts
 */

import { formatPercent2 } from "src/util/metricFormats";

export const INSIGHTS_HIERARCHY_VIEW = {
  SUPPLY: "supply",
  REPORTING_LINE: "reporting_line",
} as const;

export type InsightsHierarchyView =
  (typeof INSIGHTS_HIERARCHY_VIEW)[keyof typeof INSIGHTS_HIERARCHY_VIEW];

export const HIERARCHY_VIEW_OPTIONS = [
  {
    value: INSIGHTS_HIERARCHY_VIEW.SUPPLY,
    label: "By team owners",
    shortLabel: "Team owners",
    description:
      "Measure span and layers using each team’s primary owner — how the org is structured in TeamForm",
  },
  {
    value: INSIGHTS_HIERARCHY_VIEW.REPORTING_LINE,
    label: "By people managers",
    shortLabel: "People managers",
    description:
      "Measure span and layers using each person’s manager from HR data — independent of team owners",
  },
];

export const isSupplyHierarchySource = (source?: string | null): boolean =>
  source === INSIGHTS_HIERARCHY_VIEW.SUPPLY;

/**
 * True when Insights used the people-managers lens.
 * Accepts legacy "hris" payloads as reporting_line.
 */
export const isReportingLineHierarchySource = (
  source?: string | null
): boolean =>
  source === INSIGHTS_HIERARCHY_VIEW.REPORTING_LINE || source === "hris";

export const normalizeInsightsHierarchyView = (
  raw?: string | null
): InsightsHierarchyView | null => {
  if (!raw) return null;
  if (raw === "hris") return INSIGHTS_HIERARCHY_VIEW.REPORTING_LINE;
  if (
    raw === INSIGHTS_HIERARCHY_VIEW.SUPPLY ||
    raw === INSIGHTS_HIERARCHY_VIEW.REPORTING_LINE
  ) {
    return raw;
  }
  return null;
};

export const resolveDefaultInsightsHierarchyView = (
  raw?: string | null
): InsightsHierarchyView =>
  normalizeInsightsHierarchyView(raw) || INSIGHTS_HIERARCHY_VIEW.SUPPLY;

export const getHierarchyViewOption = (source?: string | null) => {
  const view =
    normalizeInsightsHierarchyView(source) || INSIGHTS_HIERARCHY_VIEW.SUPPLY;
  return (
    HIERARCHY_VIEW_OPTIONS.find((o) => o.value === view) ||
    HIERARCHY_VIEW_OPTIONS[0]
  );
};

export const resolveOwnershipCoverage = (args: {
  ownershipCoverage?: number | null | undefined;
  roleCounts?: { unassigned?: number | undefined } | null | undefined;
  total?: number | null | undefined;
}): number | null => {
  const { ownershipCoverage, roleCounts, total } = args;
  if (
    typeof ownershipCoverage === "number" &&
    Number.isFinite(ownershipCoverage)
  ) {
    return Math.min(1, Math.max(0, ownershipCoverage));
  }
  const pop = typeof total === "number" ? total : null;
  const unassigned = roleCounts?.unassigned;
  if (pop == null || pop <= 0 || typeof unassigned !== "number") {
    return null;
  }
  return Math.min(1, Math.max(0, (pop - unassigned) / pop));
};

export const formatOwnershipCoveragePct = (
  coverage: number | null | undefined
): string | null => {
  if (coverage == null || !Number.isFinite(coverage)) return null;
  return formatPercent2(coverage * 100);
};

export type InsightExternalManager = {
  personId?: string | null;
  span?: number | null;
  person?: {
    firstName?: string | null;
    surname?: string | null;
    preferredName?: string | null;
  } | null;
};

export type InsightWarning = {
  code?: string | null;
  count?: number | null;
};

const externalManagerName = (em: InsightExternalManager): string | null => {
  const p = em.person;
  if (!p) return null;
  const name =
    p.preferredName || [p.firstName, p.surname].filter(Boolean).join(" ");
  return name || null;
};

/**
 * D2 caption guard: the viewed team's manager sits above the population, so
 * they are excluded from roleCounts/span stats — surface them here instead.
 */
export const formatExternalManagersCaption = (
  externalManagers: InsightExternalManager[] | null | undefined
): string | null => {
  if (!externalManagers?.length) return null;
  if (externalManagers.length === 1) {
    const em = externalManagers[0]!;
    const name = externalManagerName(em) || "external manager";
    const span = typeof em.span === "number" ? em.span : null;
    return span != null
      ? `Managed by ${name} (sits above this team) — span ${span}`
      : `Managed by ${name} (sits above this team)`;
  }
  return `${externalManagers.length} managers sit above this population`;
};

/** Short user-facing fragments for supply-computation warnings (D3/D6/D7/D9/D10). */
export const formatInsightWarnings = (
  warnings: InsightWarning[] | null | undefined
): string[] => {
  if (!warnings?.length) return [];
  const fragments: string[] = [];
  for (const warning of warnings) {
    const count = typeof warning.count === "number" ? warning.count : null;
    switch (warning.code) {
      case "REQUISITIONS_EXCLUDED":
        fragments.push(
          `${count ?? "some"} requisition${count === 1 ? "" : "s"} excluded (not design capacity)`
        );
        break;
      case "NON_SUPPLY_TEAM":
        fragments.push("not a supply team — insights may not apply");
        break;
      case "MULTI_TEAM_OWNERSHIP":
        fragments.push(
          `${count ?? "some"} ${count === 1 ? "person owns" : "people own"} multiple teams — check structure`
        );
        break;
      case "EMPTY_TEAMS":
        fragments.push(
          `${count ?? "some"} empty sub-team${count === 1 ? "" : "s"} (no people, not counted as layers)`
        );
        break;
      case "OWNERSHIP_GAPS":
        fragments.push(
          `${count ?? "some"} ${count === 1 ? "person crosses" : "people cross"} an unowned level (collapsed)`
        );
        break;
      case "NO_DIRECT_OWNER":
        fragments.push(
          `${count ?? "some"} ${count === 1 ? "person has" : "people have"} no direct owner assigned`
        );
        break;
      case "VIEWED_TEAM_CHAIN_MISSES":
        fragments.push(
          `${count ?? "some"} ${count === 1 ? "person" : "people"} not measured for layers (no path to this team's owner)`
        );
        break;
      default:
        // Unknown code from a newer API: surface it rather than hiding it.
        if (warning.code)
          fragments.push(warning.code.toLowerCase().replace(/_/g, " "));
        break;
    }
  }
  return fragments;
};

/**
 * One-line caption for Team Insights surfaces.
 * Example: "Viewing: Team owners · Ownership coverage: 87%"
 */
export const formatInsightsHierarchyCaption = ({
  hierarchySource,
  selectedView,
  ownershipCoverage,
  roleCounts,
  total,
  supplyStatsTruncated,
  omitViewingPrefix = false,
  externalManagers,
  warnings,
}: {
  hierarchySource?: string | null | undefined;
  selectedView?: string | null | undefined;
  ownershipCoverage?: number | null | undefined;
  roleCounts?: { unassigned?: number | undefined } | null | undefined;
  total?: number | null | undefined;
  supplyStatsTruncated?: boolean | null | undefined;
  omitViewingPrefix?: boolean;
  externalManagers?: InsightExternalManager[] | null | undefined;
  warnings?: InsightWarning[] | null | undefined;
}): string => {
  const sourceForView =
    hierarchySource != null && hierarchySource !== ""
      ? hierarchySource
      : selectedView;
  // Don't invent "Team owners" while the workspace default lens is still
  // resolving — that flashes supply copy on reporting_line tenants.
  const normalized = normalizeInsightsHierarchyView(sourceForView);
  if (!normalized) {
    return "";
  }
  const view =
    HIERARCHY_VIEW_OPTIONS.find((o) => o.value === normalized) ??
    HIERARCHY_VIEW_OPTIONS[0]!;
  const coverage = resolveOwnershipCoverage({
    ownershipCoverage,
    roleCounts,
    total,
  });
  const coveragePct = formatOwnershipCoveragePct(coverage);
  const unassigned = roleCounts?.unassigned ?? 0;

  const parts: string[] = omitViewingPrefix
    ? [view.shortLabel]
    : [`Viewing: ${view.shortLabel}`];
  if (coveragePct != null && view.value === INSIGHTS_HIERARCHY_VIEW.SUPPLY) {
    parts.push(`Ownership coverage: ${coveragePct}`);
    if (unassigned > 0) {
      parts.push(
        `${unassigned} ${unassigned === 1 ? "person" : "people"} not yet linked`
      );
    }
    // Thin / zero coverage makes layers+span look like “0” even when the
    // chart has people — call that out so users don’t confuse it with depth.
    if (coverage != null && coverage < 0.25) {
      parts.push(
        "assign PRIMARY owners to measure layers/span (chart: manager not set)"
      );
    }
  }
  if (view.value === INSIGHTS_HIERARCHY_VIEW.SUPPLY) {
    const managedBy = formatExternalManagersCaption(externalManagers);
    if (managedBy) {
      parts.push(managedBy);
    }
    parts.push(...formatInsightWarnings(warnings));
  }
  if (supplyStatsTruncated && view.value === INSIGHTS_HIERARCHY_VIEW.SUPPLY) {
    parts.push("stats truncated (large team)");
  }
  return parts.join(" · ");
};
